import { useEffect, useState } from 'react'
import request from '@/modules/base/util/request'

import { dateConstants, errorCodes, paymentTypes } from '@/modules/base/util/constants'
import settingsConfig from '@/modules/settings/config'
import analyticsService from '@/modules/base/services/analytics'
import webstorageService from '@/modules/_services/webstorage.service'
import WebStorageKeyEnum from '@/enums/web-storage-key.enum'
import { updateSubscriptionDetails } from '@/redux/ducks/subscription/details'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
import { ShoppingCartItems } from '@/modules/moduleSubscription/types/ShoppingCart'
import type { CouponDataType } from '../../upgrade-modal/order-overview/summary/overview-summary'
import { PeriodType } from '@/redux/ducks/subscription/types/details'
import { useAppDispatch } from '@/redux/store'
import { setProductsInCart, setOrderCompleted as setOrderCompletedInStore } from '@/redux/ducks/upgrade/wizard'

export interface BillingFormData {
  companyName?: string
  street?: string
  zipCode?: string
  city?: string
  countryIso?: string
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
  bankAccountIBAN?: string
  bankAccountHolder?: string
  sepaMandate?: boolean
}

// what was actually bought - kept for the success page, independent of later cart changes
export interface CompletedOrder {
  items: ShoppingCartItems
  period: PeriodType
  couponCode?: CouponDataType
  billingData: BillingFormData
}

interface usePurchaseProps {
  billingFormData: BillingFormData,
  setIsLoading: (value: boolean) => void
  couponCode?: CouponDataType
  onUpgraded?: () => void
  preSelectedPeriod?: PeriodType
}

export default function usePurchase(props: usePurchaseProps) {
  const dispatch = useAppDispatch()
  const { billingFormData, setIsLoading, couponCode, onUpgraded, preSelectedPeriod } = props

  const [orderCompleted, setOrderCompleted] = useState<boolean>(false)
  const [completedOrder, setCompletedOrder] = useState<CompletedOrder | null>(null)
  const [productsInCart, addProductToCart] = useState<ShoppingCartItems>({})
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>(preSelectedPeriod)

  useEffect(() => {
    dispatch(setProductsInCart(productsInCart))
  }, [productsInCart])

  useEffect(() => {
    dispatch(setOrderCompletedInStore(orderCompleted))
  }, [orderCompleted])

  const handlePurchase = async() => {
    setIsLoading(true)
    let purchaseFailed = false

    const products = Object.keys(productsInCart).flatMap(type => {
      return Object.values(productsInCart[type]).map(item => item)
    })

    const subscriptions = products.filter(item => item.type)
    const analyticsServiceData = []

    const requestData = subscriptions.map(subscription => {
      const selectedPayInterval = subscription.prices.find(price => price.period === selectedPeriod)
      const paymentInterval = selectedPayInterval ? { period: selectedPeriod, ...selectedPayInterval.values[0] } : subscription.paymentInterval
      const subscriptionType = subscription.name

      analyticsServiceData.push({
        price: paymentInterval.period === dateConstants.MONTH ? paymentInterval.pricePerDuration * 12 : paymentInterval.pricePerDuration,
        type: subscriptionType,
        period: paymentInterval.period,
      })
      return {
        price: paymentInterval,
        subscriptionType,
        type: subscription.type,
        couponCode: couponCode?.code && subscriptionType === couponCode?.subscriptionType ? couponCode?.code : undefined,
      }
    })

    const modulesToBuy = Object.keys(productsInCart).reduce((result, type) => {
      result.push(
        ...Object.keys(productsInCart[type]).reduce((innerResult, name) => {
          const item = productsInCart[type][name]
          if (item.type) return innerResult
          const module = {
            type,
            name,
            duration: item.prices.find((price) => price.period === selectedPeriod)?.values?.[0]?.duration || 0,
            count: item.count,
            pricePerDuration:
              item.prices.find((price) => price.period === selectedPeriod)?.values?.[0]?.pricePerDuration || 0,
          }
          innerResult.push(module)
          return innerResult
        }, []),
      )
      return result
    }, [])

    await request(settingsConfig.subscription, {
      auth: true,
      method: 'POST',
      timeout: 60000,
      isConcurrent: true,
      data: {
        subscriptions: requestData,
        modules: modulesToBuy,
        ...billingFormData,
        paymentType: paymentTypes.DEBIT,
        period: selectedPeriod,
        // ...pick(order, REQUEST_PROPS),
      },
    }).catch(error => {
      if (error && error.body && error.body.meta
          && error.body.meta.subscriptionType
          && error.body.meta.subscriptionType[0].code
          && error.body.meta.subscriptionType[0].code === errorCodes.EXISTS) {
        return
      }
      if (error && error.body && error.body.meta
        && error.body.meta.subscriptionType
        && error.body.meta.subscriptionType[0].code
        && error.body.meta.subscriptionType[0].code === 'IS_MOBILE') {
        toast.showToast({ type: 'error', message: lang.upgradeSubscriptionErrorIsMobile })
      } else {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
      }
      purchaseFailed = true
    })
    if (!purchaseFailed) {
      updateSubscriptionDetails(true, true)
      for (const { price, type, period } of analyticsServiceData) {
        analyticsService.trackPurchase({
          price,
          type,
          period,
        })
      }
      webstorageService.removeItem(WebStorageKeyEnum.READ_ONLY_CONFIRMED)
      setCompletedOrder({ items: productsInCart, period: selectedPeriod, couponCode, billingData: billingFormData })
      setOrderCompleted(true)
      webstorageService.setItem(WebStorageKeyEnum.SHOPPING_CART, { items: {} })
      onUpgraded?.()
    }

    setIsLoading(false)
  }

  return { handlePurchase, productsInCart, addProductToCart, orderCompleted, completedOrder, selectedPeriod, setSelectedPeriod }
}
